import { useEffect, useState } from 'react';

const PRIZE_DISPLAY = '100,000,000';
const ANIMATION_INTERVAL = 4000;
const WAVE_SPEED = 120;

export function PrizePoolBanner() {
  const [waveStart, setWaveStart] = useState<number | null>(null);
  const [digitStates, setDigitStates] = useState<number[]>(
    PRIZE_DISPLAY.split('').map(() => 0)
  );

  useEffect(() => {
    const runWave = () => {
      setWaveStart(Date.now());
    };

    const initialTimeout = setTimeout(runWave, 500);
    const interval = setInterval(runWave, ANIMATION_INTERVAL);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (waveStart === null) return;

    const chars = PRIZE_DISPLAY.split('');
    const animationDuration = 400;

    const frame = () => {
      const now = Date.now();
      const elapsed = now - waveStart;

      const newStates = chars.map((char, index) => {
        if (char === ',') return 0;

        const digitStart = index * WAVE_SPEED;
        const digitElapsed = elapsed - digitStart;

        if (digitElapsed < 0) return 0;
        if (digitElapsed > animationDuration) return 0;

        const progress = digitElapsed / animationDuration;
        return Math.sin(progress * Math.PI);
      });

      setDigitStates(newStates);

      const totalDuration = (chars.length - 1) * WAVE_SPEED + animationDuration;
      if (elapsed < totalDuration) {
        requestAnimationFrame(frame);
      }
    };

    const animationId = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(animationId);
  }, [waveStart]);

  const renderDigits = () => {
    return PRIZE_DISPLAY.split('').map((char, index) => {
      const intensity = digitStates[index] || 0;
      const isComma = char === ',';

      const scale = 1 + intensity * 0.15;

      // Base: gold -> Highlight: bright white
      const r = Math.round(250 + intensity * 5);
      const g = Math.round(204 + intensity * 51);
      const b = Math.round(21 + intensity * 234);

      return (
        <span
          key={index}
          style={{
            display: 'inline-block',
            transform: isComma ? 'none' : `scale(${scale})`,
            color: isComma ? 'rgb(250, 204, 21)' : `rgb(${r}, ${g}, ${b})`,
            textShadow: isComma
              ? 'none'
              : `0 0 ${10 + intensity * 30}px rgba(255, 255, 255, ${0.1 + intensity * 0.7}), 
                 0 0 ${20 + intensity * 40}px rgba(250, 204, 21, ${0.2 + intensity * 0.5}), 
                 0 0 ${40 + intensity * 60}px rgba(250, 204, 21, ${intensity * 0.4})`,
          }}
        >
          {char}
        </span>
      );
    });
  };

  return (
    <div
      className="relative mb-4 rounded-lg p-3 text-center"
      style={{
        background:
          'linear-gradient(135deg, rgba(234, 179, 8, 0.1) 0%, rgba(251, 191, 36, 0.15) 50%, rgba(234, 179, 8, 0.1) 100%)',
        border: '1px solid rgba(234, 179, 8, 0.3)',
        boxShadow: '0 0 30px rgba(234, 179, 8, 0.1)',
      }}
    >
      <div className="mb-1 flex items-center justify-center gap-2">
        <span className="text-base">💰</span>
        <p className="text-[9px] font-semibold uppercase tracking-widest text-yellow-400/80">
          Prize Pool
        </p>
        <span className="text-base">💰</span>
      </div>
      <div className="flex items-center justify-center gap-2">
        <span className="text-lg">🏆</span>
        <div className="text-xl font-black">{renderDigits()}</div>
        <span className="text-lg">🏆</span>
      </div>
      <p className="text-xs font-bold text-yellow-400">X-CASH</p>
    </div>
  );
}
